import { Signal } from "signals";
import Components from "slotworks/components/components";
import CascadeResult from "slotworks/model/gameplay/records/results/cascade-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import Point from "slotworks/utils/geom/point";
import ReelComponent from "../../reel-component";
import AbstractReelTransition from "../abstract-reel-transition";
import ReelCascader from "./reel-cascader";

export class CascadeTransition extends AbstractReelTransition {

    public onCascadeStart: Signal = new Signal();
    public onCascadeEnd: Signal = new Signal();

    private cascadeOutDelay: number = 250;
    private cascadeInDelay: number = 250;
    private cascaders: ReelCascader[] = [];

    private cancelGroup: CancelGroup = new CancelGroup();

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]): void {
        super.init(reels, stops, reelset);
        this.reels = reels;
        this.cascaders = [];

        this.parseConfig(Components.config.matrix.transition);

        reels.forEach((reel: ReelComponent, i: number) => {
            const cascader = new ReelCascader(reel, stops, reelset);
            this.cascaders.push(cascader);
            cascader.onCascadeStart.add((index: number) => this.cascadeStartHandler(index));
            cascader.onCascadeEnd.add((index: number) => this.cascadeEndHandler(index));
        });
    }

    public destroy(): void {
        this.cascaders.forEach((cascader: ReelCascader) => {
            cascader.onCascadeStart.remove((index: number) => this.cascadeStartHandler(index));
            cascader.onCascadeEnd.remove((index: number) => this.cascadeEndHandler(index));
        });
    }

    public stop(spinRecord: SpinRecord, quickSpin: boolean = false): Contract<void> {
        const cascadeSequence: Array<() => Contract<void>> = [];
        const cascadeResults: CascadeResult[] = spinRecord.getResultsOfType(CascadeResult);
        cascadeResults.forEach((result: CascadeResult) => cascadeSequence.push(() => this.cascade(result.cascades)));

        return this.cancelGroup.sequence(cascadeSequence);
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        const skipContracts: Array<() => Contract<void>> = [];

        this.cancelGroup.cancel();
        this.cascaders.forEach((cascader: ReelCascader) => {
            const contract: Contract<void> = cascader.skip(spinRecord);
            skipContracts.push(() => contract);
        });
        return new Parallel(skipContracts);
    }

    private cascade(symbols: Point[]): Contract<void> {
        const symbolsPerReel: Point[][] = [[], [], [], [], []];
        symbols.forEach((point: Point) => symbolsPerReel[point.x].push(point));

        const cascadeOutContracts: Array<() => Contract<void>> = [];
        const cascadeInContracts: Array<() => Contract<void>> = [];
        symbolsPerReel.forEach((points: Point[], index: number) => {
            cascadeOutContracts.push(() => Contract.getDelayedContract(this.cascadeOutDelay, () => this.cascaders[index].cascadeSymbolsOut(points)));
            cascadeInContracts.push(() => Contract.getDelayedContract(this.cascadeInDelay, () => this.cascaders[index].cascadeSymbolsIn()));
        });

        const sequence: Sequence<void> = new Sequence([
            () => new Parallel(cascadeOutContracts),
            () => new Parallel(cascadeInContracts)
        ]);
        return this.cancelGroup.add(sequence);
    }

    private parseConfig(config: any): void {
        if (config.options) {
            if (config.options.cascadeOutDelay) {
                this.cascadeOutDelay = config.options.cascadeOutDelay;
            }
            if (config.options.cascadeInDelay) {
                this.cascadeInDelay = config.options.cascadeInDelay;
            }
        }
    }

    private cascadeStartHandler(reel: number): void {
        this.onCascadeStart.dispatch(reel);
    }

    private cascadeEndHandler(reel: number): void {
        this.onCascadeEnd.dispatch(reel);
    }
}
